import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import type { Context, ReactElement, ReactNode } from "react";
import { createContext, createElement, useCallback, useContext, useMemo } from "react";
import { stableStringify } from "@/shared/utils/use-held-idempotency-key";
import {
  HttpRegistrationInvitationClient,
  RegistrationInvitationApiError,
  type DirectFellowCompletionRequest,
  type InvitationCompletionResult,
  type InvitationLookupResult,
  type MemberFinalizeCompletionRequest,
  type MemberInitialCompletionRequest,
  type RegistrationCompletionRequest,
  type RegistrationInvitationClient,
} from "./registration-invitation-client";

/**
 * Query/mutation hooks and idempotency-key lifecycle for the CLMS-113 public
 * registration flow (T035). Lookup and completion both run as mutations so
 * the proof only ever lives in in-memory mutation state — never in a query
 * key or persistable query cache (UI contract proof lifecycle).
 */

const RegistrationInvitationClientContext: Context<RegistrationInvitationClient | undefined> =
  createContext<RegistrationInvitationClient | undefined>(undefined);

let defaultClient: RegistrationInvitationClient | undefined;

function getDefaultRegistrationInvitationClient(): RegistrationInvitationClient {
  defaultClient ??= new HttpRegistrationInvitationClient();
  return defaultClient;
}

export interface RegistrationInvitationClientProviderProps {
  children: ReactNode;
  client?: RegistrationInvitationClient;
}

export function RegistrationInvitationClientProvider({
  children,
  client,
}: RegistrationInvitationClientProviderProps): ReactElement {
  const value = useMemo(() => client ?? getDefaultRegistrationInvitationClient(), [client]);
  return createElement(RegistrationInvitationClientContext.Provider, { value }, children);
}

export function useRegistrationInvitationClient(): RegistrationInvitationClient {
  return (
    useContext(RegistrationInvitationClientContext) ?? getDefaultRegistrationInvitationClient()
  );
}

export type InvitationLookupMutation = UseMutationResult<InvitationLookupResult, Error, string>;

/** Shared mutationKey scope so the feature's mutations can be purged from the cache. */
const REGISTRATION_INVITATION_MUTATION_SCOPE = ["registration-invitation"] as const;

/** Resolves a captured proof to its allow-listed prefill. Never retried automatically. */
export function useInvitationLookup(): InvitationLookupMutation {
  const client = useRegistrationInvitationClient();

  return useMutation({
    mutationKey: [...REGISTRATION_INVITATION_MUTATION_SCOPE, "lookup"],
    mutationFn: (proof: string) => client.lookup({ proof }),
  });
}

/** Member `initial` body as the page holds it: the password is supplied separately. */
export type MemberInitialCompletionEnvelope = Omit<MemberInitialCompletionRequest, "password">;

/**
 * Every completion body the page retains — none carries the password (UI
 * contract L157: the password MUST NOT be part of mutation-retry variables
 * or key derivation).
 */
export type PasswordFreeCompletionRequest =
  | DirectFellowCompletionRequest
  | MemberInitialCompletionEnvelope
  | MemberFinalizeCompletionRequest;

export interface InvitationCompletionVariables {
  request: PasswordFreeCompletionRequest;
  idempotencyKey: string;
  /**
   * Member `initial` only: hands the password to the mutation exactly once,
   * at dispatch, and MUST clear its source. TanStack retains `variables`
   * (and `failureReason`) for the mutation's lifetime, so the secret is
   * composed into the wire body transiently instead of living in cache.
   */
  takePassword?: () => string | null;
}

export type InvitationCompletionMutation = UseMutationResult<
  InvitationCompletionResult,
  Error,
  InvitationCompletionVariables
>;

function isMemberInitialEnvelope(
  request: PasswordFreeCompletionRequest,
): request is MemberInitialCompletionEnvelope {
  return request.registrationVariant === "member" && request.completionMode === "initial";
}

/** Submits one logical completion attempt. Never retried automatically. */
export function useInvitationCompletion(): InvitationCompletionMutation {
  const client = useRegistrationInvitationClient();

  return useMutation({
    mutationKey: [...REGISTRATION_INVITATION_MUTATION_SCOPE, "complete"],
    mutationFn: ({ request, idempotencyKey, takePassword }: InvitationCompletionVariables) => {
      if (!isMemberInitialEnvelope(request)) {
        return client.complete(request, idempotencyKey);
      }
      const password = takePassword?.() ?? null;
      if (password === null) {
        // Defensive: an `initial` without a one-shot password is a page bug,
        // not a server outcome. Surface it as an uncertain outcome so the
        // user gets "Try again" (which sends `finalize`) rather than a
        // silently empty credential on the wire.
        return Promise.reject(
          new RegistrationInvitationApiError(
            "Member initial completion dispatched without a password.",
            0,
          ),
        );
      }
      const wireBody: MemberInitialCompletionRequest = { ...request, password };
      return client.complete(wireBody, idempotencyKey);
    },
  });
}

/**
 * Immediately evicts this feature's mutations from the TanStack mutation
 * cache. Observer `reset()` alone only schedules garbage collection after
 * `gcTime`, which would leave the raw proof (lookup variables) and the
 * completion request in cache memory after unmount/terminal states — the UI
 * contract requires clearing sensitive state at those points.
 */
export function useRegistrationInvitationCachePurge(): () => void {
  const queryClient = useQueryClient();

  return useCallback((): void => {
    const mutationCache = queryClient.getMutationCache();
    for (const mutation of mutationCache.findAll({
      mutationKey: [...REGISTRATION_INVITATION_MUTATION_SCOPE],
    })) {
      mutationCache.remove(mutation);
    }
  }, [queryClient]);
}

/**
 * Canonical fingerprint of one logical submission: the completion request
 * minus the proof (constant for the session), the FR-050 `completionMode`
 * (initial and its finalize retries are the SAME logical submission) and the
 * password (never part of the fingerprint so the secret is never hashed,
 * compared, or held past the request), with object keys sorted so field
 * order never rotates the key, and set-like reference arrays (expertise/
 * practice selections) sorted so identical selections in different orders
 * produce identical fingerprints — matching the server's canonicalized
 * idempotency fingerprint.
 */
export function createCompletionFingerprint(
  request: RegistrationCompletionRequest | PasswordFreeCompletionRequest,
): string {
  const canonical = canonicalizeReferenceSets(request) as Record<string, unknown>;
  const {
    proof: _proof,
    completionMode: _completionMode,
    password: _password,
    ...canonicalForm
  } = canonical;
  return stableStringify(canonicalForm);
}

/** Contract paths whose arrays are order-insensitive sets. */
function canonicalizeReferenceSets<
  T extends RegistrationCompletionRequest | PasswordFreeCompletionRequest,
>(request: T): T {
  switch (request.registrationVariant) {
    case "direct_fellow":
      return request;
    case "member":
      if (request.completionMode === "finalize") {
        return request;
      }
      return {
        ...request,
        memberDetails: {
          ...request.memberDetails,
          practiceAreaReferences: [...request.memberDetails.practiceAreaReferences].sort(),
        },
      };
  }
}
